import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import ParticlesBackground from '@/components/feature/ParticlesBackground';
import WhatsAppFloating from '@/components/feature/WhatsAppFloating';
import LandingTestimonials from './components/LandingTestimonials';
import LandingSuccessCases from './components/LandingSuccessCases';
import LandingBlog from './components/LandingBlog';

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
}

function RevealSection({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

const features = [
  {
    icon: 'ri-send-plane-fill',
    title: 'Disparos em Massa',
    description: 'Envie milhares de mensagens instantaneamente pelo WhatsApp com nossa API otimizada de alta performance.',
  },
  {
    icon: 'ri-user-heart-line',
    title: 'Personalização Inteligente',
    description: 'Use variáveis dinâmicas como nome, empresa e dados customizados para criar mensagens únicas para cada contato.',
  },
  {
    icon: 'ri-line-chart-fill',
    title: 'Relatórios em Tempo Real',
    description: 'Acompanhe taxa de entrega, leitura e resposta em dashboards interativos com exportação em PDF e Excel.',
  },
  {
    icon: 'ri-robot-2-line',
    title: 'Automação de Campanhas',
    description: 'Agende campanhas, configure gatilhos e automatize follow-ups sem precisar de intervenção manual.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Segurança e Compliance',
    description: 'Conexão oficial com a API do Meta. Seus dados e os de seus clientes protegidos com criptografia de ponta a ponta.',
  },
  {
    icon: 'ri-group-line',
    title: 'Segmentação Avançada',
    description: 'Crie grupos segmentados por localização, interesse e comportamento para campanhas hiper-direcionadas.',
  },
];

const stats = [
  { value: '10K+', label: 'Campanhas Enviadas' },
  { value: '98.5%', label: 'Taxa de Entrega' },
  { value: '500K+', label: 'Mensagens/mês' },
  { value: '24/7', label: 'Suporte Técnico' },
];

const steps = [
  {
    number: '01',
    title: 'Conecte seu WhatsApp',
    description: 'Vincule sua conta WhatsApp Business via API oficial do Meta em menos de 2 minutos.',
    icon: 'ri-link',
  },
  {
    number: '02',
    title: 'Importe seus Contatos',
    description: 'Faça upload de CSV ou conecte seu CRM para importar sua base de contatos automaticamente.',
    icon: 'ri-upload-cloud-line',
  },
  {
    number: '03',
    title: 'Crie sua Campanha',
    description: 'Monte mensagens personalizadas com texto, imagem e vídeo usando nosso editor visual.',
    icon: 'ri-megaphone-line',
  },
  {
    number: '04',
    title: 'Analise os Resultados',
    description: 'Acompanhe métricas em tempo real e exporte relatórios detalhados para sua equipe.',
    icon: 'ri-bar-chart-box-line',
  },
];

const plans = [
  {
    name: 'Starter',
    price: 'R$ 97',
    period: '/mês',
    features: ['5.000 mensagens/mês', '1 WhatsApp', '1 usuário', 'Importação CSV', 'Templates básicos', 'Relatório de envio', 'Suporte online'],
    highlighted: false,
    cta: 'Assinar Agora',
  },
  {
    name: 'Professional',
    price: 'R$ 297',
    period: '/mês',
    features: ['50.000 mensagens/mês', 'Até 3 WhatsApps', 'Até 5 usuários', 'Templates avançados', 'Segmentação de clientes', 'Agendamento', 'Relatórios Excel/PDF', 'Automações', 'API'],
    highlighted: true,
    cta: 'Assinar Agora',
  },
  {
    name: 'Business',
    price: 'R$ 597',
    period: '/mês',
    features: ['150.000 mensagens/mês', 'Até 5 WhatsApps', 'Até 10 usuários', 'CRM integrado', 'Funil de vendas', 'Chat em equipe', 'Automações avançadas'],
    highlighted: false,
    cta: 'Assinar Agora',
  },
  {
    name: 'Enterprise',
    price: 'Sob Consulta',
    period: '',
    features: ['500.000+ mensagens/mês', 'WhatsApps ilimitados', 'Usuários ilimitados', 'White label', 'API própria', 'Servidor dedicado', 'SLA garantido'],
    highlighted: false,
    cta: 'Falar com Vendas',
  },
];

export default function LandingPage() {
  const { user, loading } = useAuth();
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      setRedirecting(true);
    }
  }, [user, loading]);

  const scrollToSection = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const scrollToPlans = useCallback(() => {
    scrollToSection('plans');
  }, [scrollToSection]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background-50">
        <div className="w-10 h-10 border-[3px] border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (redirecting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background-50 gap-4">
        <div className="w-10 h-10 border-[3px] border-primary-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-foreground-600 text-sm">Redirecionando para o painel...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 overflow-x-hidden">
      {/* Schema.org Organization JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "WZap PRO",
            "url": "https://wzappro.com.br",
            "description": "Sistema profissional de envio e gerenciamento de campanhas via WhatsApp Business Cloud API. Gerencie contatos, crie campanhas com templates personalizados, acompanhe métricas em tempo real e automatize seus envios em massa com a API oficial do WhatsApp.",
            "areaServed": {
              "@type": "Country",
              "name": "Brasil"
            },
            "sameAs": [
              "https://wa.me/5554996542326"
            ]
          })
        }}
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-background-100/80 backdrop-blur-md border-b border-background-200/60 dark:border-background-300/30">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-[68px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
              <i className="ri-whatsapp-fill text-white text-lg" />
            </div>
            <span className="font-heading font-bold text-foreground-950 dark:text-foreground-100 text-[17px] tracking-tight">WZap PRO</span>
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <button onClick={() => scrollToSection('features')} className="text-sm text-foreground-600 dark:text-foreground-400 hover:text-foreground-900 dark:hover:text-foreground-200 transition-colors whitespace-nowrap cursor-pointer">Recursos</button>
            <button onClick={() => scrollToSection('how')} className="text-sm text-foreground-600 dark:text-foreground-400 hover:text-foreground-900 dark:hover:text-foreground-200 transition-colors whitespace-nowrap cursor-pointer">Como Funciona</button>
            <button onClick={() => scrollToSection('testimonials')} className="text-sm text-foreground-600 dark:text-foreground-400 hover:text-foreground-900 dark:hover:text-foreground-200 transition-colors whitespace-nowrap cursor-pointer">Depoimentos</button>
            <button onClick={scrollToPlans} className="text-sm text-foreground-600 dark:text-foreground-400 hover:text-foreground-900 dark:hover:text-foreground-200 transition-colors whitespace-nowrap cursor-pointer">Planos</button>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="text-sm font-medium text-foreground-700 dark:text-foreground-300 hover:text-foreground-950 dark:hover:text-foreground-100 transition-colors whitespace-nowrap"
            >
              Entrar
            </Link>
            <Link
              to="/login"
              className="text-sm font-medium bg-primary-500 hover:bg-primary-600 text-white px-5 py-2.5 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md active:scale-[0.98] whitespace-nowrap"
            >
              Testar Grátis
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-[68px] min-h-[620px] md:min-h-[700px] flex items-center">
        {/* Background image overlay */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20modern%20geometric%20background%20with%20soft%20green%20and%20teal%20gradients%2C%20smooth%20flowing%20shapes%2C%20organic%20curves%2C%20minimal%20clean%20composition%2C%20subtle%20texture%2C%20bright%20and%20airy%20atmosphere%2C%20corporate%20but%20creative%20feel%2C%20no%20text%20or%20people%2C%20light%20background%20dominant&width=1800&height=900&seq=landing-hero-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-background-50 dark:to-[#08080d]" />
        </div>

        <div className="relative z-10 w-full max-w-4xl mx-auto px-5 md:px-8 text-center py-16 md:py-20">
          {/* Particles canvas */}
          <ParticlesBackground />
          <RevealSection>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 dark:bg-white/5 backdrop-blur-sm border border-white/20 mb-8">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-sm text-white/90">WhatsApp Marketing + CRM + Automação</span>
            </div>
          </RevealSection>

          <RevealSection delay={100}>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading font-extrabold text-white leading-[1.05] tracking-tight mb-6">
              Comunique-se em<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 via-teal-300 to-white">massa pelo WhatsApp</span>
            </h1>
          </RevealSection>

          <RevealSection delay={200}>
            <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed">
              A plataforma mais completa para campanhas de WhatsApp Marketing. Conecte sua própria API oficial Meta ou use nossa infraestrutura gerenciada. Disparos em massa, templates, relatórios e multi-instância própria.
            </p>
          </RevealSection>

          <RevealSection delay={300}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/login"
                className="w-full sm:w-auto bg-primary-500 hover:bg-primary-600 text-white text-base font-semibold px-8 py-3.5 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md active:scale-[0.98] whitespace-nowrap inline-block text-center"
              >
                Testar Grátis — R$ 97/mês
              </Link>
              <a
                href="#how"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById('how');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="w-full sm:w-auto bg-white/10 hover:bg-white/15 backdrop-blur-sm border border-white/20 text-white text-base font-medium px-8 py-3.5 rounded-xl transition-all duration-200 whitespace-nowrap inline-flex items-center justify-center cursor-pointer"
              >
                <i className="ri-play-circle-line mr-2" />
                Ver Demonstração
              </a>
            </div>
          </RevealSection>

          <RevealSection delay={500}>
            <div className="flex items-center justify-center gap-2 mt-10">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/30 backdrop-blur-md border border-white/10 text-white/90 text-sm font-semibold">
                <i className="ri-shield-check-line text-emerald-400" />
                <span>Escolha sua forma de conexão • Setup em 2 minutos</span>
              </span>
            </div>
          </RevealSection>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-1.5">
            <div className="w-1.5 h-3 rounded-full bg-white/60 animate-bounce" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-16 md:py-24 px-5 md:px-8">
        <div className="max-w-7xl mx-auto">
          <RevealSection>
            <div className="text-center mb-16">
              <span className="inline-block text-xs font-bold text-primary-600 dark:text-primary-400 uppercase tracking-[0.2em] mb-3">Recursos</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-4">
                Tudo que você precisa para<br />escalar suas campanhas
              </h2>
              <p className="text-foreground-500 dark:text-foreground-400 max-w-xl mx-auto text-base">
                Ferramentas profissionais pensadas para empresas que querem resultados reais com WhatsApp Marketing.
              </p>
            </div>
          </RevealSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <RevealSection key={i} delay={i * 80}>
                <div className="group bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-6 md:p-7 hover:border-primary-200 dark:hover:border-primary-500/30 transition-all duration-300 h-full">
                  <div className="w-11 h-11 rounded-xl bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${f.icon} text-xl text-primary-600 dark:text-primary-400`} />
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-950 dark:text-foreground-100 mb-2">{f.title}</h3>
                  <p className="text-sm text-foreground-500 dark:text-foreground-400 leading-relaxed">{f.description}</p>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 md:py-16 bg-primary-500 dark:bg-primary-600">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <RevealSection key={i} delay={i * 100}>
                <div className="text-center">
                  <p className="text-3xl md:text-4xl font-heading font-extrabold text-white mb-1">{s.value}</p>
                  <p className="text-sm text-white/70">{s.label}</p>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-16 md:py-24 px-5 md:px-8 bg-background-50 dark:bg-[#08080d]">
        <div className="max-w-7xl mx-auto">
          <RevealSection>
            <div className="text-center mb-16">
              <span className="inline-block text-xs font-bold text-primary-600 dark:text-primary-400 uppercase tracking-[0.2em] mb-3">Como Funciona</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-4">
                Do setup à primeira campanha<br />em menos de 5 minutos
              </h2>
            </div>
          </RevealSection>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative">
            {/* Connector line desktop */}
            <div className="hidden md:block absolute top-[60px] left-[12%] right-[12%] h-0.5 bg-background-200 dark:bg-background-300 z-0" />
            {steps.map((s, i) => (
              <RevealSection key={i} delay={i * 120}>
                <div className="relative z-10 text-center">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-white dark:bg-background-100 border-2 border-primary-500 flex items-center justify-center mb-5">
                    <i className={`${s.icon} text-xl text-primary-600 dark:text-primary-400`} />
                  </div>
                  <span className="text-[11px] font-bold text-primary-500 dark:text-primary-400 uppercase tracking-widest mb-2 block">{s.number}</span>
                  <h3 className="text-base font-heading font-semibold text-foreground-950 dark:text-foreground-100 mb-2">{s.title}</h3>
                  <p className="text-sm text-foreground-500 dark:text-foreground-400 leading-relaxed max-w-[240px] mx-auto">{s.description}</p>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="plans" className="py-16 md:py-24 px-5 md:px-8">
        <div className="max-w-7xl mx-auto">
          <RevealSection>
            <div className="text-center mb-16">
              <span className="inline-block text-xs font-bold text-primary-600 dark:text-primary-400 uppercase tracking-[0.2em] mb-3">Planos</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-4">
                Planos que crescem com<br />seu negócio
              </h2>
              <p className="text-foreground-500 dark:text-foreground-400 max-w-xl mx-auto text-base">
                Escolha seu plano. Use sua própria API Meta ou nossa infraestrutura. Créditos de envio vendidos separadamente.
              </p>
            </div>
          </RevealSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {plans.map((p, i) => (
              <RevealSection key={i} delay={i * 100}>
                <div
                  className={`relative bg-white dark:bg-background-100 rounded-2xl border p-5 md:p-6 h-full flex flex-col ${
                    p.highlighted
                      ? 'border-primary-500 ring-4 ring-primary-500/10'
                      : 'border-background-200 dark:border-background-300'
                  }`}
                >
                  {p.highlighted && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-500 text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                      Mais Popular
                    </span>
                  )}
                  <h3 className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-2">{p.name}</h3>
                  <div className="mb-6">
                    <span className="text-3xl font-heading font-extrabold text-foreground-950 dark:text-foreground-100">{p.price}</span>
                    <span className="text-sm text-foreground-500 dark:text-foreground-400">{p.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {p.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm text-foreground-600 dark:text-foreground-400">
                        <i className="ri-check-line text-emerald-500 mt-0.5 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {p.name === 'Enterprise' ? (
                    <a
                      href="https://wa.me/5554996542326?text=Olá!%20Tenho%20interesse%20no%20plano%20Enterprise%20do%20WZap%20PRO.%20Gostaria%20de%20mais%20informações%20sobre%20white%20label,%20API%20própria%20e%20servidor%20dedicado."
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full text-center text-sm font-semibold px-5 py-3 rounded-xl bg-background-50 dark:bg-background-200 text-foreground-700 dark:text-foreground-300 hover:bg-background-100 dark:hover:bg-background-300 border border-background-200 dark:border-background-300 transition-all duration-200 whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      <i className="ri-mail-line" />
                      {p.cta}
                    </a>
                  ) : (
                    <Link
                      to="/login"
                      className={`w-full text-center text-sm font-semibold px-5 py-3 rounded-xl transition-all duration-200 whitespace-nowrap ${
                        p.highlighted
                          ? 'bg-primary-500 hover:bg-primary-600 text-white shadow-sm hover:shadow-md active:scale-[0.98]'
                          : 'bg-background-50 dark:bg-background-200 text-foreground-700 dark:text-foreground-300 hover:bg-background-100 dark:hover:bg-background-300 border border-background-200 dark:border-background-300'
                      }`}
                    >
                      {p.cta}
                    </Link>
                  )}
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <LandingTestimonials />

      {/* Success Cases */}
      <LandingSuccessCases />

      {/* Blog */}
      <LandingBlog />

      {/* CTA */}
      <section className="py-16 md:py-20 relative">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20abstract%20gradient%20background%20with%20deep%20green%20and%20teal%20tones%2C%20smooth%20flowing%20shapes%2C%20minimal%20modern%20composition%2C%20subtle%20glowing%20particles%2C%20elegant%20corporate%20feel%2C%20dark%20moody%20atmosphere%2C%20no%20text&width=1800&height=600&seq=landing-cta-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-8 text-center">
          <RevealSection>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              WhatsApp Marketing a partir de R$ 97/mês
            </h2>
            <p className="text-white/70 text-base md:text-lg mb-8 max-w-xl mx-auto">
              Junte-se a mais de 2.000 empresas que usam WZap PRO. Conecte sua API ou use a nossa — você escolhe.
            </p>
            <Link
              to="/login"
              className="inline-flex bg-white hover:bg-white/90 text-foreground-950 text-base font-bold px-8 py-3.5 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md active:scale-[0.98] whitespace-nowrap"
            >
              Criar Conta Gratuita
              <i className="ri-arrow-right-line ml-2" />
            </Link>
          </RevealSection>
        </div>
      </section>

      {/* WhatsApp Floating */}
      <WhatsAppFloating />

      {/* Footer */}
      <footer className="bg-background-100 dark:bg-background-100 border-t border-background-200 dark:border-background-300 py-12 px-5 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                <i className="ri-whatsapp-fill text-white text-base" />
              </div>
              <span className="font-heading font-bold text-foreground-950 dark:text-foreground-100 text-base">WZap PRO</span>
            </Link>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-foreground-500 dark:text-foreground-400">
              <button onClick={() => scrollToSection('features')} className="hover:text-foreground-800 dark:hover:text-foreground-200 transition-colors whitespace-nowrap cursor-pointer">Recursos</button>
              <button onClick={() => scrollToSection('how')} className="hover:text-foreground-800 dark:hover:text-foreground-200 transition-colors whitespace-nowrap cursor-pointer">Como Funciona</button>
              <button onClick={() => scrollToSection('testimonials')} className="hover:text-foreground-800 dark:hover:text-foreground-200 transition-colors whitespace-nowrap cursor-pointer">Depoimentos</button>
              <button onClick={scrollToPlans} className="hover:text-foreground-800 dark:hover:text-foreground-200 transition-colors whitespace-nowrap cursor-pointer">Planos</button>
              <Link to="/login" className="hover:text-foreground-800 dark:hover:text-foreground-200 transition-colors whitespace-nowrap">Entrar</Link>
            </div>
            <div className="flex flex-wrap justify-center gap-4 text-xs text-foreground-400">
              <Link to="/privacy" className="hover:text-foreground-600 transition-colors">Privacidade</Link>
              <span className="text-foreground-300">•</span>
              <Link to="/terms" className="hover:text-foreground-600 transition-colors">Termos</Link>
              <span className="text-foreground-300">•</span>
              <Link to="/faq" className="hover:text-foreground-600 transition-colors">FAQ</Link>
              <span className="text-foreground-300">•</span>
              <Link to="/about" className="hover:text-foreground-600 transition-colors">Sobre</Link>
              <span className="text-foreground-300">•</span>
              <Link to="/contact" className="hover:text-foreground-600 transition-colors">Contato</Link>
              <span className="text-foreground-300">•</span>
              <button onClick={() => scrollToSection('blog')} className="hover:text-foreground-600 transition-colors cursor-pointer">Blog</button>
            </div>
            <p className="text-xs text-foreground-400 text-center">
              &copy; {new Date().getFullYear()} WZap PRO. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}